import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { endOfDay, isValid, startOfDay } from "date-fns";

import type { RootState } from "@/store";

export type CalendarViewsType = "month" | "week" | "work_week" | "day" | "agenda";

export type CalendarReducerType = {
  ownerId: Nullable<string>;
  creator: Nullable<SmallUser>;
  eventType: string[];
  eventStatus: Nullable<EventStatusType>;
  from: Nullable<Date>;
  to: Nullable<Date>;
  insertedStart: Nullable<Date>;
  insertedStop: Nullable<Date>;
  shiftedAtStart: Nullable<Date>;
  shiftedAtEnd: Nullable<Date>;
  canceledAtStart: Nullable<Date>;
  canceledAtEnd: Nullable<Date>;

  teamId: null | string;
  page: number;

  sendEvents: {
    from: Nullable<Date>;
    to: Nullable<Date>;
    type: string[];
    sentStatus: Nullable<boolean>;
    eventStatus: Nullable<EventStatusType>;
  };

  feedback: {
    ownerId: null | string;
    filterAttrs: Record<string, AttributeFilterType>;
    done: boolean;
    page: number;
    tookPlace: Nullable<boolean>;
  };
};

const initialState: CalendarReducerType = {
  ownerId: null,
  creator: null,
  eventType: [],
  eventStatus: "NOT_CLEARED",
  from: null,
  to: null,
  insertedStart: null,
  insertedStop: null,
  shiftedAtStart: null,
  shiftedAtEnd: null,
  canceledAtStart: null,
  canceledAtEnd: null,
  teamId: null,
  page: 0,

  sendEvents: {
    from: startOfDay(new Date()),
    to: endOfDay(new Date()),
    type: [],
    sentStatus: null,
    eventStatus: null,
  },

  feedback: {
    ownerId: null,
    filterAttrs: {},
    done: false,
    tookPlace: null,
    page: 0,
  },
};

const todosSlice = createSlice({
  name: "calendar/calendar",
  initialState,
  reducers: {
    setOwnerId(state, action: PayloadAction<Nullable<string>>) {
      state.ownerId = action.payload;
    },
    setCreator(state, action: PayloadAction<Nullable<SmallUser>>) {
      state.creator = action.payload;
    },
    setEventType(state, action: PayloadAction<string[]>) {
      state.eventType = action.payload;
    },
    setEventStatus(state, action: PayloadAction<Nullable<EventStatusType>>) {
      state.eventStatus = action.payload;
    },
    setFrom(state, action: PayloadAction<Nullable<Date>>) {
      state.from = action.payload && isValid(action.payload) ? startOfDay(action.payload) : null;
    },
    setTo(state, action: PayloadAction<Nullable<Date>>) {
      state.to = action.payload && isValid(action.payload) ? endOfDay(action.payload) : null;
    },
    setInsertedStart(state, action: PayloadAction<Nullable<Date>>) {
      state.insertedStart = action.payload && isValid(action.payload) ? startOfDay(action.payload) : null;
    },
    setInsertedStop(state, action: PayloadAction<Nullable<Date>>) {
      state.insertedStop = action.payload && isValid(action.payload) ? endOfDay(action.payload) : null;
    },

    setTeamId(state, action: PayloadAction<Nullable<string>>) {
      state.teamId = action.payload;
    },
    setPage(state, { payload }: PayloadAction<number>) {
      state.page = payload;
    },
    setFeedbackPage(state, { payload }: PayloadAction<number>) {
      state.feedback.page = payload;
    },
    setFeedbackAttributeFilter(state, action: PayloadAction<{ name: string; value: Nilable<AttributeFilterType> }>) {
      if (!action.payload.value) {
        delete state.feedback.filterAttrs[action.payload.name];
      } else {
        state.feedback.filterAttrs[action.payload.name] = action.payload.value;
      }
    },
    toggleFeedbackDone(state) {
      state.feedback.done = !state.feedback.done;
    },
    setFeedbackOwnerId(state, action: PayloadAction<null | string>) {
      state.feedback.ownerId = action.payload;
    },
    setSendEventsFrom(state, action: PayloadAction<Date>) {
      state.sendEvents.from = action.payload ? startOfDay(action.payload) : null;
    },
    setSendEventsTo(state, action: PayloadAction<Date>) {
      state.sendEvents.to = action.payload ? endOfDay(action.payload) : null;
    },
    setSendEventsType(state, action: PayloadAction<string[]>) {
      state.sendEvents.type = action.payload;
    },
    setSendEventStatus(state, action: PayloadAction<Nullable<EventStatusType>>) {
      state.sendEvents.eventStatus = action.payload;
    },
    setSendEventsSentStatus(state, { payload }: PayloadAction<Nullable<boolean>>) {
      state.sendEvents.sentStatus = payload;
    },
    setTookPlace(state, { payload }: PayloadAction<Nullable<boolean>>) {
      state.feedback.tookPlace = payload;
    },
    setShiftedAtStart(state, action: PayloadAction<Nullable<Date>>) {
      state.shiftedAtStart = action.payload && isValid(action.payload) ? startOfDay(action.payload) : null;
    },
    setShiftedAtEnd(state, action: PayloadAction<Nullable<Date>>) {
      state.shiftedAtEnd = action.payload && isValid(action.payload) ? endOfDay(action.payload) : null;
    },
    setCanceledAtStart(state, action: PayloadAction<Nullable<Date>>) {
      state.canceledAtStart = action.payload && isValid(action.payload) ? startOfDay(action.payload) : null;
    },
    setCanceledAtEnd(state, action: PayloadAction<Nullable<Date>>) {
      state.canceledAtEnd = action.payload && isValid(action.payload) ? endOfDay(action.payload) : null;
    },
    resetFeedback(state) {
      state.feedback = initialState.feedback;
      state.ownerId = null;
      state.creator = null;
      state.teamId = null;
    },

    resetSendEvents(state) {
      state.sendEvents = initialState.sendEvents;
    },

    resetFilters(state) {
      state.ownerId = null;
      state.creator = null;
      state.eventType = [];
      state.eventStatus = "NOT_CLEARED";
      state.from = null;
      state.to = null;
      state.insertedStart = null;
      state.insertedStop = null;
      state.shiftedAtStart = null;
      state.shiftedAtEnd = null;
      state.canceledAtStart = null;
      state.canceledAtEnd = null;
      state.teamId = null;
    },
  },
});

export const {
  setOwnerId,
  setCreator,
  setEventType,
  setEventStatus,
  setFrom,
  setTo,
  setInsertedStart,
  setInsertedStop,
  setTeamId,
  setFeedbackAttributeFilter,
  toggleFeedbackDone,
  setFeedbackOwnerId,
  setSendEventsFrom,
  setSendEventsTo,
  setSendEventsType,
  setSendEventsSentStatus,
  setSendEventStatus,
  setPage,
  setFeedbackPage,
  resetFeedback,
  resetSendEvents,
  resetFilters,
  setTookPlace,
  setShiftedAtStart,
  setShiftedAtEnd,
  setCanceledAtStart,
  setCanceledAtEnd,
} = todosSlice.actions;
export const calendarSelector = (state: RootState) => state.calendar;
export default todosSlice.reducer;
